import React, { useEffect, useState } from 'react';
import { Card } from '@playground/ui';
import { apiFetch } from '@/platform/api.js';

type AiProvider = 'anthropic' | 'openai' | 'gemini';

interface AdminSettings {
  siteName?: string;
  maintenanceMode: boolean;
  aiProvider: AiProvider;
  aiModel: string;
  aiEnabled: boolean;
  anthropicApiKeySet?: boolean;
  anthropicApiKeyPreview?: string | null;
  openaiApiKeySet?: boolean;
  openaiApiKeyPreview?: string | null;
  geminiApiKeySet?: boolean;
  geminiApiKeyPreview?: string | null;
  cloudflareZoneId?: string;
  cloudflareApiTokenSet?: boolean;
  cloudflareApiTokenPreview?: string | null;
}

const PROVIDER_OPTIONS: { value: AiProvider; label: string }[] = [
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai',    label: 'OpenAI (ChatGPT)' },
  { value: 'gemini',    label: 'Google Gemini' },
];

const DEFAULT_MODELS: Record<AiProvider, string> = {
  anthropic: 'claude-sonnet-4-6',
  openai:    'gpt-4o',
  gemini:    'gemini-1.5-flash',
};

const MODEL_HINTS: Record<AiProvider, string> = {
  anthropic: 'e.g. claude-sonnet-4-6, claude-opus-4-6, claude-haiku-4-5-20251001',
  openai:    'e.g. gpt-4o, gpt-4o-mini, gpt-4-turbo',
  gemini:    'e.g. gemini-1.5-flash, gemini-1.5-pro, gemini-2.0-flash',
};

const KEY_PLACEHOLDERS: Record<AiProvider, string> = {
  anthropic: 'sk-ant-…',
  openai:    'sk-…',
  gemini:    'AIza…',
};

const KEY_FIELD: Record<AiProvider, string> = {
  anthropic: 'anthropicApiKey',
  openai:    'openaiApiKey',
  gemini:    'geminiApiKey',
};

const KEY_SET_FIELD: Record<AiProvider, keyof AdminSettings> = {
  anthropic: 'anthropicApiKeySet',
  openai:    'openaiApiKeySet',
  gemini:    'geminiApiKeySet',
};

const KEY_PREVIEW_FIELD: Record<AiProvider, keyof AdminSettings> = {
  anthropic: 'anthropicApiKeyPreview',
  openai:    'openaiApiKeyPreview',
  gemini:    'geminiApiKeyPreview',
};

export function Settings({ token }: { token: string }) {
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  /** Fatal load failure — replaces the whole panel. */
  const [error, setError] = useState(false);
  /** Non-fatal save failure — shown inline so typed values are preserved. */
  const [saveError, setSaveError] = useState('');

  // Pending API key inputs — one per provider, never pre-filled
  const [apiKeys, setApiKeys] = useState<Record<AiProvider, string>>({
    anthropic: '',
    openai: '',
    gemini: '',
  });

  // Track which keys have been explicitly cleared via the ✕ button
  const [clearedKeys, setClearedKeys] = useState<Set<AiProvider>>(new Set());

  // Cloudflare
  const [cfZoneId, setCfZoneId] = useState('');
  const [cfApiToken, setCfApiToken] = useState('');
  const [cfTokenCleared, setCfTokenCleared] = useState(false);
  const [cfSaving, setCfSaving] = useState(false);
  const [cfSaved, setCfSaved] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    apiFetch('/api/admin/config', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error('Unauthorized');
        return r.json() as Promise<{ settings: AdminSettings }>;
      })
      .then((d) => {
        setSettings(d.settings);
        setCfZoneId(d.settings.cloudflareZoneId ?? '');
      })
      .catch(() => setError(true));
  }, [token]);

  async function fetchConfig(): Promise<Record<string, unknown>> {
    const r = await apiFetch('/api/admin/config', { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error(`Could not read current config (${r.status})`);
    return r.json() as Promise<Record<string, unknown>>;
  }

  async function save(updated: AdminSettings) {
    setSaving(true);
    setSaved(false);
    setSaveError('');
    try {
      const config = await fetchConfig();
      const settingsToSend: Record<string, unknown> = { ...updated };

      // Include new key values; include "" for explicitly cleared keys
      for (const provider of ['anthropic', 'openai', 'gemini'] as AiProvider[]) {
        const val = apiKeys[provider];
        if (val) {
          settingsToSend[KEY_FIELD[provider]] = val;
        } else if (clearedKeys.has(provider)) {
          settingsToSend[KEY_FIELD[provider]] = '';
        }
      }

      const res = await apiFetch('/api/admin/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...config, settings: settingsToSend }),
      });
      // `apiFetch` does not throw on 4xx/5xx. Bail before clearing the key
      // inputs — otherwise a rejected save wipes the freshly-typed secret and
      // still reports "✓ Saved".
      if (!res.ok) throw new Error(`Save rejected (${res.status})`);

      // Update "set" indicators and clear entered / cleared keys
      const keySetUpdates: Partial<AdminSettings> = {};
      for (const provider of ['anthropic', 'openai', 'gemini'] as AiProvider[]) {
        if (apiKeys[provider]) {
          (keySetUpdates as Record<string, boolean>)[KEY_SET_FIELD[provider] as string] = true;
        } else if (clearedKeys.has(provider)) {
          (keySetUpdates as Record<string, boolean>)[KEY_SET_FIELD[provider] as string] = false;
          (keySetUpdates as Record<string, unknown>)[KEY_PREVIEW_FIELD[provider] as string] = null;
        }
      }
      setApiKeys({ anthropic: '', openai: '', gemini: '' });
      setClearedKeys(new Set());
      setSettings({ ...updated, ...keySetUpdates });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  }

  async function saveCf() {
    setCfSaving(true);
    setCfSaved(false);
    setSaveError('');
    try {
      const config = await fetchConfig();
      const existingSettings = (config['settings'] as Record<string, unknown>) ?? {};
      const res = await apiFetch('/api/admin/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...config,
          settings: {
            ...existingSettings,
            cloudflareZoneId: cfZoneId,
            ...(cfApiToken ? { cloudflareApiToken: cfApiToken } : cfTokenCleared ? { cloudflareApiToken: '' } : {}),
          },
        }),
      });
      if (!res.ok) throw new Error(`Save rejected (${res.status})`);

      if (cfApiToken) setCfApiToken('');
      if (cfTokenCleared) {
        setCfTokenCleared(false);
        if (settings) setSettings({ ...settings, cloudflareApiTokenSet: false, cloudflareApiTokenPreview: null });
      }
      setCfSaved(true);
      setTimeout(() => setCfSaved(false), 2000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save Cloudflare settings.');
    } finally {
      setCfSaving(false);
    }
  }

  async function purgeCfCache() {
    setPurging(true);
    setPurgeResult(null);
    try {
      // Auto-save any pending CF values before purging
      if (cfZoneId || cfApiToken) {
        await saveCf();
      }
      const res = await apiFetch('/api/admin/cloudflare/purge', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json() as { ok: boolean; error?: string };
      setPurgeResult({ ok: data.ok, msg: data.ok ? 'Cache purged successfully!' : (data.error ?? 'Purge failed.') });
    } catch {
      setPurgeResult({ ok: false, msg: 'Network error.' });
    } finally {
      setPurging(false);
    }
  }

  if (error) return <p className="text-red-500 text-sm">Failed to load settings.</p>;
  if (!settings) return <p className="text-gray-500 dark:text-gray-400 text-sm">Loading…</p>;

  const provider = settings.aiProvider ?? 'anthropic';
  const isKeySet = settings[KEY_SET_FIELD[provider]] as boolean | undefined;

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Settings</h2>

      {/* A save failure must be visible next to the action, not swallowed. */}
      {saveError && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {saveError}
        </p>
      )}

      {/* Maintenance mode */}
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h3 id="maintenance-label" className="font-semibold text-gray-900 dark:text-gray-100">Maintenance Mode</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Hide all games from the lobby. Existing rooms continue to work.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={settings.maintenanceMode}
            aria-labelledby="maintenance-label"
            onClick={() => void save({ ...settings, maintenanceMode: !settings.maintenanceMode })}
            disabled={saving}
            className={[
              'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
              settings.maintenanceMode ? 'bg-amber-500' : 'bg-gray-200 dark:bg-gray-600',
              saving ? 'opacity-50' : '',
            ].join(' ')}
          >
            <span aria-hidden="true" className={[
              'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition duration-200 ease-in-out',
              settings.maintenanceMode ? 'translate-x-5' : 'translate-x-0',
            ].join(' ')} />
          </button>
        </div>
      </Card>

      {/* AI Integration */}
      <Card>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">AI Integration</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Powers AI-generated words for Hangman and Draw &amp; Guess, plus contextual hints.
        </p>

        {/* Enable toggle */}
        <div className="flex items-center justify-between mb-4">
          <span id="ai-enabled-label" className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable AI features</span>
          <button
            type="button"
            role="switch"
            aria-checked={Boolean(settings.aiEnabled)}
            aria-labelledby="ai-enabled-label"
            onClick={() => void save({ ...settings, aiEnabled: !settings.aiEnabled })}
            disabled={saving}
            className={[
              'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
              settings.aiEnabled ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600',
              saving ? 'opacity-50' : '',
            ].join(' ')}
          >
            <span className={[
              'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition duration-200 ease-in-out',
              settings.aiEnabled ? 'translate-x-5' : 'translate-x-0',
            ].join(' ')} />
          </button>
        </div>

        <div className="space-y-3">
          {/* Provider selector */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="ai-provider">
              AI Provider
            </label>
            <select
              id="ai-provider"
              value={provider}
              onChange={(e) => {
                const p = e.target.value as AiProvider;
                void save({ ...settings, aiProvider: p, aiModel: DEFAULT_MODELS[p] });
              }}
              disabled={saving}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-500"
            >
              {PROVIDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Model text input */}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="ai-model">
              Model
            </label>
            <input
              id="ai-model"
              type="text"
              value={settings.aiModel}
              onChange={(e) => setSettings({ ...settings, aiModel: e.target.value })}
              onBlur={(e) => void save({ ...settings, aiModel: e.target.value })}
              disabled={saving}
              placeholder={DEFAULT_MODELS[provider]}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-500"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{MODEL_HINTS[provider]}</p>
          </div>

          {/* API keys — one per provider */}
          {(['anthropic', 'openai', 'gemini'] as AiProvider[]).map((p) => {
            const keySet = settings[KEY_SET_FIELD[p]] as boolean | undefined;
            const keyPreview = settings[KEY_PREVIEW_FIELD[p]] as string | null | undefined;
            return (
              <div key={p}>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor={`key-${p}`}>
                  {PROVIDER_OPTIONS.find((o) => o.value === p)?.label} API Key
                  {keySet && keyPreview && (
                    <span className="ml-2 text-xs font-mono text-green-700 dark:text-green-400 font-normal">
                      ● {keyPreview}
                    </span>
                  )}
                </label>
                <div className="mt-1 flex gap-2">
                  <input
                    id={`key-${p}`}
                    type="password"
                    value={apiKeys[p]}
                    onChange={(e) => {
                      setApiKeys({ ...apiKeys, [p]: e.target.value });
                      if (clearedKeys.has(p)) setClearedKeys(new Set([...clearedKeys].filter((k) => k !== p)));
                    }}
                    placeholder={keySet && !clearedKeys.has(p) ? 'Enter new key to replace…' : KEY_PLACEHOLDERS[p]}
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-500"
                  />
                  {keySet && !clearedKeys.has(p) && (
                    <button
                      type="button"
                      title="Clear this key"
                      onClick={() => {
                        setApiKeys({ ...apiKeys, [p]: '' });
                        setClearedKeys(new Set([...clearedKeys, p]));
                      }}
                      className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-500 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                    >
                      ✕ Clear
                    </button>
                  )}
                  {clearedKeys.has(p) && (
                    <span className="flex items-center text-xs text-red-500 dark:text-red-400">Will be cleared</span>
                  )}
                </div>
              </div>
            );
          })}

          <p className="text-xs text-gray-500 dark:text-gray-400">Leave key fields blank to keep existing keys. Use ✕ Clear to delete a key.</p>
        </div>

        <button
          type="button"
          onClick={() => void save({ ...settings })}
          disabled={saving}
          className="mt-4 w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save AI Settings'}
        </button>
      </Card>

      {/* Cloudflare */}
      <Card>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Cloudflare</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Purge the Cloudflare CDN cache when you push updates or toggle maintenance mode.
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="cf-zone">Zone ID</label>
            <input
              id="cf-zone"
              type="text"
              value={cfZoneId}
              onChange={(e) => setCfZoneId(e.target.value)}
              placeholder="e.g. 023e105f4ecef8ad9ca31a8372d0c353"
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-500"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="cf-token">
              API Token
              {settings.cloudflareApiTokenSet && settings.cloudflareApiTokenPreview && (
                <span className="ml-2 text-xs font-mono text-green-700 dark:text-green-400 font-normal">
                  ● {settings.cloudflareApiTokenPreview}
                </span>
              )}
            </label>
            <div className="mt-1 flex gap-2">
              <input
                id="cf-token"
                type="password"
                value={cfApiToken}
                onChange={(e) => { setCfApiToken(e.target.value); if (cfTokenCleared) setCfTokenCleared(false); }}
                placeholder={settings.cloudflareApiTokenSet && !cfTokenCleared ? 'Enter new token to replace…' : 'Token with Cache Purge permission'}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-500"
              />
              {settings.cloudflareApiTokenSet && !cfTokenCleared && (
                <button
                  type="button"
                  title="Clear this token"
                  onClick={() => { setCfApiToken(''); setCfTokenCleared(true); }}
                  className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-500 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                >
                  ✕ Clear
                </button>
              )}
              {cfTokenCleared && (
                <span className="flex items-center text-xs text-red-500 dark:text-red-400">Will be cleared</span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => void saveCf()}
            disabled={cfSaving}
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {cfSaving ? 'Saving…' : cfSaved ? '✓ Saved' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => void purgeCfCache()}
            disabled={purging}
            className="flex-1 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50"
          >
            {purging ? 'Purging…' : 'Purge Cache'}
          </button>
        </div>

        {purgeResult && (
          <p className={['mt-2 text-sm font-medium text-center', purgeResult.ok ? 'text-green-600' : 'text-red-600'].join(' ')}>
            {purgeResult.msg}
          </p>
        )}
      </Card>
    </div>
  );
}
